import { Badge, Button, Spinner } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Download } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { AccountHolderCell, EuroCell } from '@/components/data-table/cells';
import { FilterChips } from '@/components/data-table/column-filter-bar';
import { ColumnFilterHeader } from '@/components/data-table/column-filter-header';
import { DataTable } from '@/components/data-table/data-table';
import { useReportCsv } from '@/hooks/queries/reports/use-report-csv';
import { type ColumnFilterConfig, useColumnFilters } from '@/hooks/use-column-filters';
import { getErrorMessage } from '@/lib/api/error';
import { uniqueSorted } from '@/utils/array';
import { downloadCsv, rowsToCsv } from '@/utils/csv';
import { extractFileName } from '@/utils/string';
import { ReportOverviewCards } from './report-overview-cards';

const PAGE_SIZE = 50;
// Columns with at most this many distinct values filter as a checkbox list; the rest as free text.
const CHOICE_THRESHOLD = 25;

type CsvRow = string[];

const ASSET_AMOUNT_FORMAT = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 8 });

const formatAssetAmount = (amount: string): string => {
  const value = Number(amount);
  return Number.isFinite(value) ? ASSET_AMOUNT_FORMAT.format(value) : amount;
};

const CsvCell = ({ value }: { value: string }) => {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <span className="whitespace-nowrap">{value}</span>;
};
CsvCell.displayName = 'CsvCell';

/** `COIN:amount|COIN:amount` balance blobs rendered as one badge per asset. */
const AssetsCell = ({ value }: { value: string }) => {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex max-w-md flex-wrap gap-1">
      {value.split('|').map((entry) => {
        const [coin, amount = ''] = entry.split(':');
        return (
          <Badge className="gap-1 font-mono text-[11px]" key={coin} title={`${coin} ${amount}`}>
            <span className="font-semibold">{coin}</span>
            <span className="tabular-nums">{formatAssetAmount(amount)}</span>
          </Badge>
        );
      })}
    </div>
  );
};
AssetsCell.displayName = 'AssetsCell';

/** Pipe-joined `YYYY-MM-DD` terms versions rendered as one badge per acceptance. */
const TermsAcceptedCell = ({ value }: { value: string }) => {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex max-w-md flex-wrap gap-1">
      {value.split('|').map((version) => (
        <Badge className="font-mono text-[11px] tabular-nums" key={version}>
          {version}
        </Badge>
      ))}
    </div>
  );
};
TermsAcceptedCell.displayName = 'TermsAcceptedCell';

const EurStringCell = ({ value }: { value: string }) => {
  const parsed = Number(value);
  if (value === '' || !Number.isFinite(parsed)) return <CsvCell value={value} />;
  return <EuroCell value={parsed} />;
};
EurStringCell.displayName = 'EurStringCell';

// Audit-report columns holding EUR amounts: rendered as euros, filtered and sorted numerically.
const EUR_COLUMN_KEYS = new Set(['totaleur', 'fiateur', 'cryptoeur']);

// Well-known audit-report columns get a richer treatment; everything else renders as text.
const SPECIAL_CELLS: Record<string, (value: string) => ReactNode> = {
  pid: (value) => <AccountHolderCell value={value || undefined} />,
  totaleur: (value) => <EurStringCell value={value} />,
  fiateur: (value) => <EurStringCell value={value} />,
  cryptoeur: (value) => <EurStringCell value={value} />,
  assets: (value) => <AssetsCell value={value} />,
  termsaccepted: (value) => <TermsAcceptedCell value={value} />,
};

// Numeric sort for the EUR columns ('90' must not sort above '1500.5'); blanks and junk go last when ascending.
export const compareNumericCells = (a: string, b: string): number => {
  const left = a.trim() === '' ? Number.NaN : Number(a);
  const right = b.trim() === '' ? Number.NaN : Number(b);
  if (!Number.isFinite(left)) return Number.isFinite(right) ? 1 : 0;
  if (!Number.isFinite(right)) return -1;
  return left - right;
};

const splitTokens = (value: string): string[] => value.split('|').filter(Boolean);
const splitAssetCoins = (value: string): string[] =>
  value
    .split('|')
    .map((entry) => entry.split(':')[0])
    .filter(Boolean);

const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}([T ]\S.*)?$/;

/** A column filters as 'date' when it has values and every non-empty cell is ISO-date-shaped. */
export const isDateColumn = (rows: CsvRow[], index: number): boolean => {
  let hasValue = false;
  for (const row of rows) {
    const value = (row[index] ?? '').trim();
    if (!value) continue;
    if (!ISO_DATE_SHAPE.test(value)) return false;
    hasValue = true;
  }
  return hasValue;
};

interface DerivedFilters {
  filterConfigs: ColumnFilterConfig<CsvRow>[];
  /** Checkbox-list values per 'choice'/'tokens' field. */
  optionsByField: Record<string, string[]>;
}

/**
 * Well-known audit columns get dedicated filter kinds — terms/assets filter on
 * their individual pipe-separated values (any/all), totalEur filters numerically,
 * date-shaped columns filter by preset/custom ranges — and everything else falls
 * back to the distinct-count heuristic.
 */
const deriveFilters = (headers: string[], rows: CsvRow[]): DerivedFilters => {
  const filterConfigs: ColumnFilterConfig<CsvRow>[] = [];
  const optionsByField: Record<string, string[]> = {};

  headers.forEach((header, index) => {
    const field = `col-${index}`;
    const key = header.trim().toLowerCase();
    const getText = (row: CsvRow) => row[index] ?? '';

    if (key === 'termsaccepted' || key === 'assets') {
      const getTokens = (row: CsvRow) => (key === 'assets' ? splitAssetCoins(row[index] ?? '') : splitTokens(row[index] ?? ''));
      filterConfigs.push({ field, title: header, filter: 'tokens', getText, getTokens });
      optionsByField[field] = uniqueSorted(rows.flatMap(getTokens));
      return;
    }
    if (EUR_COLUMN_KEYS.has(key)) {
      filterConfigs.push({ field, title: header, filter: 'number', getText });
      return;
    }
    if (isDateColumn(rows, index)) {
      filterConfigs.push({ field, title: header, filter: 'date', getText });
      return;
    }

    const distinct = new Set<string>();
    for (const row of rows) {
      const value = row[index];
      if (value) distinct.add(value);
      if (distinct.size > CHOICE_THRESHOLD) break;
    }
    const isChoice = distinct.size > 0 && distinct.size <= CHOICE_THRESHOLD;
    filterConfigs.push({ field, title: header, filter: isChoice ? 'choice' : 'text', getText });
    if (isChoice) optionsByField[field] = uniqueSorted([...distinct]);
  });

  return { filterConfigs, optionsByField };
};

export interface ReportCsvViewerProps {
  fileKey: string;
}

/**
 * Renders a CSV report file from S3 as a filterable data table with overview
 * cards. Columns and filter configs are derived from the file's header row at
 * runtime, so any CSV report type works without a per-report column list.
 * Filters live in each column header (spreadsheet-style funnel popovers).
 */
export const ReportCsvViewer = ({ fileKey }: ReportCsvViewerProps) => {
  const csvQuery = useReportCsv(fileKey);
  const { data, isPending, isError, error, refetch } = csvQuery;

  const headers = data?.headers;
  const rows = data?.rows;

  const { filterConfigs, optionsByField } = useMemo(() => deriveFilters(headers ?? [], rows ?? []), [headers, rows]);

  const filterState = useColumnFilters(rows, filterConfigs);
  const filteredRows = filterState.filteredData;

  // Not memoised: filterState is a fresh object every render, so the header
  // filter controls would defeat any memo. Renders here are driven only by
  // query/filter state changes, and DataTable's sort state lives outside columns.
  const columns = (headers ?? []).map((header, index): ColumnDef<CsvRow, unknown> => {
    const key = header.trim().toLowerCase();
    const renderSpecial = SPECIAL_CELLS[key];
    const config = filterConfigs[index];
    return {
      id: `col-${index}`,
      header,
      accessorFn: (row) => row[index] ?? '',
      ...(EUR_COLUMN_KEYS.has(key) ? { sortingFn: (a, b, columnId) => compareNumericCells(String(a.getValue(columnId)), String(b.getValue(columnId))) } : {}),
      meta: config ? { filterControl: <ColumnFilterHeader config={config} options={optionsByField[config.field]} state={filterState} /> } : undefined,
      cell: ({ row }) => {
        const value = row.original[index] ?? '';
        return renderSpecial ? renderSpecial(value) : <CsvCell value={value} />;
      },
    };
  });

  const handleExport = () => {
    if (!headers || !filteredRows) return;
    const baseName = extractFileName(fileKey).replace(/\.csv$/i, '');
    downloadCsv(rowsToCsv(headers, filteredRows), `${baseName}-filtered.csv`);
  };

  if (isPending) {
    return (
      <div className="flex items-center justify-center gap-3 py-16 text-muted-foreground">
        <Spinner className="size-5" />
        <span className="text-sm">Loading report…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-3">
        <p className="text-sm text-red-500">Failed to load report file: {getErrorMessage(error)}</p>
        <Button onClick={() => refetch()} size="sm" type="button" variant="outline">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {headers && filteredRows ? <ReportOverviewCards headers={headers} rows={filteredRows} totalRowCount={rows?.length ?? 0} /> : null}
      <FilterChips clearAll={filterState.clearAll} configs={filterConfigs} filters={filterState.filters} removeFilter={filterState.removeFilter} />
      {rows ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-muted-foreground text-sm tabular-nums">
            {filteredRows?.length ?? 0} of {rows.length} rows
          </p>
          <Button className="gap-2" disabled={!filteredRows?.length} onClick={handleExport} size="sm" type="button" variant="outline">
            <Download className="size-4" />
            Export filtered CSV
          </Button>
        </div>
      ) : null}
      <DataTable columns={columns} data={filteredRows} emptyMessage="No rows match the current filters." pageSize={PAGE_SIZE} stableColumnWidths stickyHeader surface={false} />
    </div>
  );
};
ReportCsvViewer.displayName = 'ReportCsvViewer';
